import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ViewChild, ElementRef, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { Subscription } from 'rxjs';
import { DocumentViewerService } from '../../services/document-viewer.service';
import { DocumentViewerConfig, DocumentStatus } from '../../types/document-viewer.types';
import { NgIconComponent } from '@ng-icons/core';


@Component({
  selector: 'app-document-viewer',
  standalone: true,
  imports: [CommonModule, NgxExtendedPdfViewerModule,
    NgIconComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './document-viewer.component.html',
  styleUrls: ['./document-viewer.component.scss']
})
export class DocumentViewerComponent implements OnInit, OnDestroy {
  @ViewChild('docxContainer', { static: false }) docxContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('txtContainer', { static: false }) txtContainer!: ElementRef<HTMLPreElement>;

  isOpen = false;
  config: DocumentViewerConfig | null = null;
  status: DocumentStatus = DocumentStatus.LOADING;
  pdfSrc: string | null = null;
  imageSrc: string | null = null;
  errorMessage: string | null = null;
  showUnsupportedFormat: boolean = false;

  private subscription!: Subscription;
  private currentBlobUrl: string | null = null;

  DocumentStatus = DocumentStatus; // Expose enum to template

  constructor(private viewerService: DocumentViewerService) {}

  ngOnInit(): void {
    this.subscription = this.viewerService.viewerState$.subscribe(
      async (state) => {
        const wasOpen = this.isOpen;
        const previousConfig = this.config;

        this.isOpen = state.isOpen;
        this.config = state.config;
        this.status = state.status;

        // Only render if state changed from closed to open, or if config changed
        if (this.isOpen && this.config && (!wasOpen || previousConfig !== this.config)) {
          await this.renderDocument();
        } else if (!this.isOpen && wasOpen) {
          this.cleanup();
        }
      }
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.cleanup();
  }

  /**
   * Render document based on file type
   */
  private async renderDocument(): Promise<void> {
    if (!this.config) return;

    try {
      this.errorMessage = null;
      this.status = DocumentStatus.LOADING;

      // Get blob from URL or use provided blob
      let blob: Blob;
      if (this.config.fileBlob) {
        blob = this.config.fileBlob;
      } else if (this.config.fileUrl) {
        blob = await this.viewerService.fetchDocument(this.config.fileUrl);
        // Store fetched blob so download button works
        this.config.fileBlob = blob;
      } else {
        throw new Error('No file source provided');
      }

      // Render based on file type
      switch (this.config.fileType) {
        case 'pdf':
          await this.renderPdf(blob);
          break;

        case 'docx':
          await this.renderDocx(blob);
          break;

        case 'doc':
        case 'excel':
          // Old .doc and Excel formats are not supported for preview - show fallback
          this.showUnsupportedFormat = true;
          break;

        case 'txt':
          await this.renderTxt(blob);
          break;

        case 'image':
          await this.renderImage(blob);
          break;

        default:
          throw new Error(`Unsupported file type: ${this.config.fileType}`);
      }

      this.status = DocumentStatus.READY;
    } catch (error) {
      console.error('Error rendering document:', error);
      this.errorMessage = 'Failed to load document. Please try again.';
      this.status = DocumentStatus.ERROR;
    }
  }

  /**
   * Render PDF using ngx-extended-pdf-viewer
   */
  private async renderPdf(blob: Blob): Promise<void> {
    this.cleanup();
    this.currentBlobUrl = URL.createObjectURL(blob);
    this.pdfSrc = this.currentBlobUrl;
  }

  /**
   * Render DOCX using docx-preview
   */
  private async renderDocx(blob: Blob): Promise<void> {
    // Wait for view to render
    setTimeout(async () => {
      if (this.docxContainer?.nativeElement) {
        try {
          await this.viewerService.renderDocx(
            blob,
            this.docxContainer.nativeElement,
            false // Set to true to use mammoth.js fallback
          );
        } catch (error) {
          console.error('docx-preview failed, trying mammoth.js:', error);
          // Fallback to mammoth.js
          await this.viewerService.renderDocx(
            blob,
            this.docxContainer.nativeElement,
            true
          );
        }
      }
    }, 100);
  }

  /**
   * Render TXT file
   */
  private async renderTxt(blob: Blob): Promise<void> {
    setTimeout(async () => {
      if (this.txtContainer?.nativeElement) {
        await this.viewerService.renderTxt(
          blob,
          this.txtContainer.nativeElement
        );
      }
    }, 100);
  }

  /**
   * Render Image file
   */
  private async renderImage(blob: Blob): Promise<void> {
    this.cleanup();
    this.currentBlobUrl = URL.createObjectURL(blob);
    this.imageSrc = this.currentBlobUrl;
  }

  /**
   * Close viewer modal
   */
  close(): void {
    this.viewerService.closeDocument();
  }

  /**
   * Download current document
   */
  download(): void {
    if (this.config?.fileBlob && this.config.fileName) {
      this.viewerService.downloadDocument(
        this.config.fileBlob,
        this.config.fileName
      );
    }
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
      this.currentBlobUrl = null;
    }
    this.pdfSrc = null;
    this.imageSrc = null;
    this.errorMessage = null;
    this.showUnsupportedFormat = false;
  }

  /**
   * Handle backdrop click (close on click outside)
   */
  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }
}
